"use client"
import { MOBILE_SIDEBAR_LINKS, SIDEBAR_LINKS, getSidebarLinks } from '../../constants/content'
import Image from 'next/image'
import Link from 'next/link'
import React from 'react'
import { Button } from '../ui/button'
import { SidebarTrigger, useSidebar } from '../ui/sidebar'
import { threeDotsIcon } from '../../constants/images'
import { usePathname } from 'next/navigation'
import { cn, buildRegionAwareHref, removeRegionFromPath } from '../../lib/utils'
import { useSession } from '../../../features/auth/context/session.context'
import { useIsGroupContact } from '@/features/stm/hooks/use-is-group-contact'
import { useRegion } from '../../hooks/use-region'
import { UserRole } from '../../constants/user-roles'
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip'
import { PROFILE_NAV_LOCK_TOOLTIP, useNavLockedUntilProfileComplete } from '@/features/profile/hooks/use-nav-profile-lock'
import ProfileNavLockDialog from './profile-nav-lock-dialog'

function MobileSidebar() {

    const { toggleSidebar } = useSidebar()
    const pathname = usePathname()
    const { session } = useSession()
    const { isGroupContact, inAnyGroup } = useIsGroupContact()
    const { regionSlug } = useRegion()
    const pathWithoutRegion = React.useMemo(() => {
        if (regionSlug) {
            return removeRegionFromPath(pathname)
        }
        return pathname
    }, [regionSlug, pathname])
    
    // Get role-aware mobile sidebar links
    const getMobileSidebarLinks = () => {
        if (!session) return MOBILE_SIDEBAR_LINKS
        
        const allSidebarLinks = getSidebarLinks(session.role, isGroupContact || inAnyGroup)
        
        // Volunteers: show all 5 links (no "More" button)
        if (session.role === UserRole.VOLUNTEER) {
            return allSidebarLinks.slice(0, 5)
        }
        
        // Base Leader and Admin on mobile: same 5 as Volunteers (Reports, Hub, Homeowners, Work Orders, Volunteers), no Admin icon, no More
        if (session.role === UserRole.BASE_LEADER || session.role === UserRole.ADMIN) {
            return allSidebarLinks.slice(0, 5)
        }
        
        // Team Leader: 5th icon = Volunteers (if associated with a group) or Partners (if not). No "More" button.
        if (session.role === UserRole.TEAM_LEADER) {
            return allSidebarLinks.slice(0, 5)
        }
        
        // Other roles (e.g. Partner): first 4 links + "More" button
        return allSidebarLinks.slice(0, 4)
    }
    
    const mobileSidebarLinks = getMobileSidebarLinks()
    const navLocked = useNavLockedUntilProfileComplete()
    const [profileLockDialogOpen, setProfileLockDialogOpen] = React.useState(false)
    const isVolunteer = session?.role === UserRole.VOLUNTEER
    const isBaseLeaderOrAdmin = session?.role === UserRole.BASE_LEADER || session?.role === UserRole.ADMIN
    const isTeamLeader = session?.role === UserRole.TEAM_LEADER
    const showMoreButton = !isVolunteer && !isBaseLeaderOrAdmin && !isTeamLeader

    return (
        <>
        <div className="fixed inset-x-0 bottom-0 z-50 grid w-full grid-cols-5 items-center justify-center bg-background pb-1 md:hidden print:hidden">
            {mobileSidebarLinks.map((sidebarLink, index) => {
                const href = sidebarLink.isRegionAware ? buildRegionAwareHref(regionSlug, sidebarLink.url) : sidebarLink.url
                const pathOnly = sidebarLink.url.split('?')[0]
                const normalizedUrl = pathOnly.startsWith('/') ? pathOnly : `/${pathOnly}`
                const isActive = sidebarLink.isRegionAware
                    ? pathWithoutRegion === normalizedUrl || pathWithoutRegion.startsWith(`${normalizedUrl}/`)
                    : pathname === normalizedUrl || pathname.startsWith(`${normalizedUrl}/`)
                const itemClassName = cn(
                    `flex flex-col items-center justify-center p-2 text-[10px] text-center border-t-2 border-white`,
                    {
                        "border-primary bg-primary/10": isActive,
                    },
                    navLocked && "cursor-not-allowed opacity-50"
                )
                if (navLocked) {
                    return (
                        <Tooltip key={`mobile-sidebar-link-${sidebarLink.name}-${index}`}>
                            <TooltipTrigger asChild>
                                <button
                                    type="button"
                                    className={itemClassName}
                                    aria-disabled="true"
                                    onClick={() => setProfileLockDialogOpen(true)}
                                >
                                    <Image 
                                        src={sidebarLink.icon}
                                        alt={`${sidebarLink.name} icon`}
                                        className='w-7 h-7'
                                    />
                                    {sidebarLink.name}
                                </button>
                            </TooltipTrigger>
                            <TooltipContent side="top">{PROFILE_NAV_LOCK_TOOLTIP}</TooltipContent>
                        </Tooltip>
                    )
                }
                return (
                    <Link 
                        href={href}
                        className={itemClassName}
                        key={`mobile-sidebar-link-${sidebarLink.name}-${index}`}
                    >
                        <Image 
                            src={sidebarLink.icon}
                            alt={`${sidebarLink.name} icon`}
                            className='w-7 h-7'
                        />
                        {sidebarLink.name}
                    </Link>
                )
            })}
            {showMoreButton && (
                <div 
                    className="flex flex-col items-center justify-center p-2 text-[10px]"
                    onClick={toggleSidebar}
                >
                    <Image 
                        src={threeDotsIcon}
                        alt={`${SIDEBAR_LINKS[4].name} icon`}
                        className='w-7 h-7 text-[10px]'
                    />
                    {"More"}    
                </div>
            )}
        </div>
        <ProfileNavLockDialog
            open={profileLockDialogOpen}
            onOpenChange={setProfileLockDialogOpen}
        />
        </>
    )
}


export default MobileSidebar